/**
 * Dashboard Filters Component
 * Improved UX with grouped filters and better organization
 */

import React, { useState, useMemo } from "react";
import { useDashboardFilters } from "../contexts/DashboardFilterContext";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import { X, Filter, Search, ChevronDown, ChevronUp } from "lucide-react";
import { Badge } from "../../ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Checkbox } from "../../ui/checkbox";
import { getStates } from "../../../lib/utils";
import { Separator } from "../../ui/separator";

const PLAN_TYPES = [
  { value: "fers", label: "FERS" },
  { value: "csrs", label: "CSRS" },
  { value: "401k", label: "401(k)" },
  { value: "403b", label: "403(b)" },
  { value: "none", label: "No Plan" },
];

const LIFECYCLE_STAGES = [
  { value: "prospect", label: "Prospect" },
  { value: "initial_outreach", label: "Initial Outreach" },
  { value: "benefits_analysis", label: "Benefits Analysis" },
  { value: "proposal_sent", label: "Proposal Sent" },
  { value: "customer", label: "Customer" },
  { value: "lost", label: "Lost" },
];

const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
  { value: "Pending", label: "Pending" },
];

export const DashboardFilters: React.FC = () => {
  const { filters, updateFilters } = useDashboardFilters();
  const states = getStates();
  const [stateSearchTerm, setStateSearchTerm] = useState("");
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Filter states based on search
  const filteredStates = useMemo(() => {
    if (!stateSearchTerm) return states.filter((s) => s.value !== "");
    return states.filter(
      (state) =>
        state.value !== "" &&
        (state.label.toLowerCase().includes(stateSearchTerm.toLowerCase()) ||
          state.value.toLowerCase().includes(stateSearchTerm.toLowerCase()))
    );
  }, [states, stateSearchTerm]);

  const toggleGroup = (group: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(group)) {
      newExpanded.delete(group);
    } else {
      newExpanded.add(group);
    }
    setExpandedGroups(newExpanded);
  };

  const handleAgeRangeChange = (field: "min" | "max", value: string) => {
    const numValue = value === "" ? undefined : parseInt(value);
    const currentRange = filters.ageRange || [undefined, undefined];
    
    if (field === "min") {
      updateFilters({
        ageRange: [numValue, currentRange[1]] as [number, number],
      });
    } else {
      updateFilters({
        ageRange: [currentRange[0], numValue] as [number, number],
      });
    }
  };

  const handleClearFilter = (filterKey: keyof typeof filters) => {
    updateFilters({ [filterKey]: undefined } as any);
  };

  const handleMultiSelect = (
    filterKey: "planType" | "lifecycleStage" | "status",
    value: string,
    checked: boolean
  ) => {
    const currentValues = filters[filterKey] || [];
    if (checked) {
      updateFilters({
        [filterKey]: [...currentValues, value] as any,
      });
    } else {
      updateFilters({
        [filterKey]: currentValues.filter((v) => v !== value) as any,
      });
    }
  };

  const hasActiveFilters =
    filters.ageRange?.[0] !== undefined ||
    filters.ageRange?.[1] !== undefined ||
    filters.city ||
    filters.stateProvince ||
    (filters.planType && filters.planType.length > 0) ||
    filters.federalAgency ||
    (filters.lifecycleStage && filters.lifecycleStage.length > 0) ||
    (filters.status && filters.status.length > 0);

  const activeFilterCount = [
    filters.ageRange?.[0] !== undefined || filters.ageRange?.[1] !== undefined,
    !!filters.city,
    !!filters.stateProvince,
    (filters.planType && filters.planType.length > 0),
    !!filters.federalAgency,
    (filters.lifecycleStage && filters.lifecycleStage.length > 0),
    (filters.status && filters.status.length > 0),
  ].filter(Boolean).length;

  const clearAllFilters = () => {
    updateFilters({
      ageRange: undefined,
      city: undefined,
      stateProvince: undefined,
      planType: undefined,
      federalAgency: undefined,
      lifecycleStage: undefined,
      status: undefined,
    });
    setStateSearchTerm("");
  };

  return (
    <div className="space-y-3">
      {/* Header with active filters summary */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-semibold">Filters</span>
          {hasActiveFilters && (
            <Badge variant="secondary" className="text-xs font-medium bg-accent/10 text-accent border-accent/20">
              {activeFilterCount} active
            </Badge>
          )}
        </div>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className="text-xs h-7 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3 mr-1.5" />
            Clear All
          </Button>
        )}
      </div>

      {/* Active Filters Badges - Compact horizontal display */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 pb-2">
          {filters.ageRange && (filters.ageRange[0] !== undefined || filters.ageRange[1] !== undefined) && (
            <Badge variant="secondary" className="text-xs px-2 py-1 flex items-center gap-1.5">
              Age: {filters.ageRange[0] ?? 'Any'}-{filters.ageRange[1] ?? 'Any'}
              <button
                onClick={() => handleClearFilter("ageRange")}
                className="ml-0.5 hover:bg-destructive/20 rounded-full p-0.5 transition-colors"
                aria-label="Remove age filter"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.city && (
            <Badge variant="secondary" className="text-xs px-2 py-1 flex items-center gap-1.5">
              City: {filters.city}
              <button
                onClick={() => handleClearFilter("city")}
                className="ml-0.5 hover:bg-destructive/20 rounded-full p-0.5 transition-colors"
                aria-label="Remove city filter"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.stateProvince && (
            <Badge variant="secondary" className="text-xs px-2 py-1 flex items-center gap-1.5">
              State: {states.find((s) => s.value === filters.stateProvince)?.label || filters.stateProvince}
              <button
                onClick={() => handleClearFilter("stateProvince")}
                className="ml-0.5 hover:bg-destructive/20 rounded-full p-0.5 transition-colors"
                aria-label="Remove state filter"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.planType && filters.planType.length > 0 && (
            <Badge variant="secondary" className="text-xs px-2 py-1 flex items-center gap-1.5">
              Plan: {filters.planType.length}
              <button
                onClick={() => handleClearFilter("planType")}
                className="ml-0.5 hover:bg-destructive/20 rounded-full p-0.5 transition-colors"
                aria-label="Remove plan type filter"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.federalAgency && (
            <Badge variant="secondary" className="text-xs px-2 py-1 flex items-center gap-1.5">
              Agency: {filters.federalAgency}
              <button
                onClick={() => handleClearFilter("federalAgency")}
                className="ml-0.5 hover:bg-destructive/20 rounded-full p-0.5 transition-colors"
                aria-label="Remove agency filter"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.lifecycleStage && filters.lifecycleStage.length > 0 && (
            <Badge variant="secondary" className="text-xs px-2 py-1 flex items-center gap-1.5">
              Stage: {filters.lifecycleStage.length}
              <button
                onClick={() => handleClearFilter("lifecycleStage")}
                className="ml-0.5 hover:bg-destructive/20 rounded-full p-0.5 transition-colors"
                aria-label="Remove lifecycle stage filter"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.status && filters.status.length > 0 && (
            <Badge variant="secondary" className="text-xs px-2 py-1 flex items-center gap-1.5">
              Status: {filters.status.length}
              <button
                onClick={() => handleClearFilter("status")}
                className="ml-0.5 hover:bg-destructive/20 rounded-full p-0.5 transition-colors"
                aria-label="Remove status filter"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
        </div>
      )}

      <Separator />

      {/* Grouped Filters */}
      <div className="space-y-3">
        {/* Demographics Group */}
        <div className="space-y-2">
          <button
            onClick={() => toggleGroup("demographics")}
            className="flex items-center justify-between w-full text-left"
          >
            <Label className="text-sm font-semibold cursor-pointer">Demographics</Label>
            {expandedGroups.has("demographics") ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
          {expandedGroups.has("demographics") && (
            <div className="space-y-3 pl-2">
              {/* Age Range */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground">Age Range</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={filters.ageRange?.[0] || ""}
                    onChange={(e) => handleAgeRangeChange("min", e.target.value)}
                    min={0}
                    max={120}
                    className="h-8 text-sm flex-1"
                  />
                  <span className="text-xs text-muted-foreground">to</span>
                  <Input
                    type="number"
                    placeholder="Max"
                    value={filters.ageRange?.[1] || ""}
                    onChange={(e) => handleAgeRangeChange("max", e.target.value)}
                    min={0}
                    max={120}
                    className="h-8 text-sm flex-1"
                  />
                </div>
              </div>

              {/* Location */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* State/Province */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground">State</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="h-8 w-full justify-between text-sm font-normal"
                      >
                        <span className="truncate text-xs">
                          {filters.stateProvince
                            ? states.find((s) => s.value === filters.stateProvince)?.label || filters.stateProvince
                            : "All states"}
                        </span>
                        <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[280px] p-0" align="start">
                      <div className="p-2 border-b">
                        <div className="relative">
                          <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
                          <Input
                            placeholder="Search states..."
                            value={stateSearchTerm}
                            onChange={(e) => setStateSearchTerm(e.target.value)}
                            className="pl-8 h-8 text-sm"
                          />
                        </div>
                      </div>
                      <div className="max-h-[250px] overflow-y-auto">
                        <div
                          className="p-2 hover:bg-accent cursor-pointer transition-colors"
                          onClick={() => {
                            updateFilters({ stateProvince: undefined });
                            setStateSearchTerm("");
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <Checkbox checked={!filters.stateProvince} />
                            <span className="text-xs">All states</span>
                          </div>
                        </div>
                        {filteredStates.map((state) => (
                          <div
                            key={state.value}
                            className="p-2 hover:bg-accent cursor-pointer transition-colors"
                            onClick={() => {
                              updateFilters({ stateProvince: state.value });
                              setStateSearchTerm("");
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <Checkbox checked={filters.stateProvince === state.value} />
                              <span className="text-xs">{state.label}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* City */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground">City</Label>
                  <Input
                    placeholder="Filter by city"
                    value={filters.city || ""}
                    onChange={(e) => updateFilters({ city: e.target.value || undefined })}
                    className="h-8 text-sm"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <Separator />

        {/* Plans & Status Group */}
        <div className="space-y-2">
          <button
            onClick={() => toggleGroup("plans")}
            className="flex items-center justify-between w-full text-left"
          >
            <Label className="text-sm font-semibold cursor-pointer">Plans & Status</Label>
            {expandedGroups.has("plans") ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
          {expandedGroups.has("plans") && (
            <div className="space-y-3 pl-2">
              {/* Plan Type */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground">Retirement Plan Type</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-8 w-full justify-between text-sm font-normal"
                    >
                      <span className="truncate text-xs">
                        {filters.planType && filters.planType.length > 0
                          ? `${filters.planType.length} selected`
                          : "All plan types"}
                      </span>
                      <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[220px] p-0" align="start">
                    <div className="p-2 space-y-1">
                      {PLAN_TYPES.map((plan) => (
                        <div
                          key={plan.value}
                          className="flex items-center space-x-2 p-1.5 hover:bg-accent rounded cursor-pointer transition-colors"
                          onClick={() =>
                            handleMultiSelect(
                              "planType",
                              plan.value,
                              !filters.planType?.includes(plan.value)
                            )
                          }
                        >
                          <Checkbox
                            checked={filters.planType?.includes(plan.value) || false}
                          />
                          <Label className="text-xs font-normal cursor-pointer">
                            {plan.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Federal Agency */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground">Federal Agency</Label>
                <Input
                  placeholder="Filter by agency"
                  value={filters.federalAgency || ""}
                  onChange={(e) =>
                    updateFilters({ federalAgency: e.target.value || undefined })
                  }
                  className="h-8 text-sm"
                />
              </div>

              {/* Lifecycle Stage */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground">Lifecycle Stage</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-8 w-full justify-between text-sm font-normal"
                    >
                      <span className="truncate text-xs">
                        {filters.lifecycleStage && filters.lifecycleStage.length > 0
                          ? `${filters.lifecycleStage.length} selected`
                          : "All stages"}
                      </span>
                      <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[220px] p-0" align="start">
                    <div className="p-2 space-y-1">
                      {LIFECYCLE_STAGES.map((stage) => (
                        <div
                          key={stage.value}
                          className="flex items-center space-x-2 p-1.5 hover:bg-accent rounded cursor-pointer transition-colors"
                          onClick={() =>
                            handleMultiSelect(
                              "lifecycleStage",
                              stage.value,
                              !filters.lifecycleStage?.includes(stage.value)
                            )
                          }
                        >
                          <Checkbox
                            checked={filters.lifecycleStage?.includes(stage.value) || false}
                          />
                          <Label className="text-xs font-normal cursor-pointer">
                            {stage.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Status */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground">Status</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-8 w-full justify-between text-sm font-normal"
                    >
                      <span className="truncate text-xs">
                        {filters.status && filters.status.length > 0
                          ? `${filters.status.length} selected`
                          : "All statuses"}
                      </span>
                      <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[180px] p-0" align="start">
                    <div className="p-2 space-y-1">
                      {STATUS_OPTIONS.map((status) => (
                        <div
                          key={status.value}
                          className="flex items-center space-x-2 p-1.5 hover:bg-accent rounded cursor-pointer transition-colors"
                          onClick={() =>
                            handleMultiSelect(
                              "status",
                              status.value,
                              !filters.status?.includes(status.value)
                            )
                          }
                        >
                          <Checkbox
                            checked={filters.status?.includes(status.value) || false}
                          />
                          <Label className="text-xs font-normal cursor-pointer">
                            {status.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
